import React from 'react';
import { GameHeader } from '@precisionpros/game-ui';
import LighthouseIcon from './components/LighthouseIcon';
import SeaBoard from './components/SeaBoard';
import LeaderboardPage from './pages/LeaderboardPage';
import { fetchDailyPuzzle, fetchTodaySession, performSessionAction, startDailySession } from './api/game';
import GAME_CONFIG from './config/game';
import { HUB_URL } from './config/environment';
import { CONDITIONS } from './game/conditions';
import { hasLighthouse } from './game/rules';
import { createGameState } from './game/state';

const feedbackMessages = {
  'geometric-conflict': 'That position is already in a lighthouse beam. One strike added. The red highlight clears automatically; no need to tap it again.',
  'territory-conflict': 'That visible territory already has a lighthouse. One strike added. The red highlight clears automatically; no need to tap it again.',
  'invalid-completion': 'That arrangement is not solved yet. Undo or revise a placement and keep reasoning.',
  'beam-preview': 'Beam paths flash for 3 seconds. Tap this lighthouse again to remove it.',
  placed: 'Lighthouse placed. Its beams flash for 3 seconds.',
  removed: 'Lighthouse removed.',
  undo: 'Last lighthouse action undone.',
  redo: 'Lighthouse action restored.',
  completed: 'All waters charted.',
};

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

function CompletionDialog({ elapsed, strikes, onClose }) {
  return (
    <div className="sea-success-backdrop" onClick={onClose}>
      <section
        className="sea-success-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sea-success-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sea-success-icon" aria-hidden="true">✓</div>
        <p className="eyebrow">Voyage complete</p>
        <h2 id="sea-success-title">All waters charted!</h2>
        <p className="sea-success-message">You placed all eight Lighthouses correctly.</p>
        <div className="sea-success-stats">
          <span><small>TIME</small><strong>{formatTime(elapsed)}</strong></span>
          <span><small>STRIKES</small><strong>{strikes}</strong></span>
          <span><small>RANK</small><strong>{strikes === 0 ? 'Flawless Beacon' : 'Navigator'}</strong></span>
        </div>
        <a className="primary-action sea-success-leaderboard" href={`${window.location.origin}/leaderboard`}>
          View leaderboard
        </a>
        <button type="button" className="sea-success-close" onClick={onClose}>Close</button>
      </section>
    </div>
  );
}

function App() {
  if (window.location.pathname.replace(/\/+$/, '') === '/leaderboard') {
    return <LeaderboardPage />;
  }
  return <GamePage />;
}

function GamePage() {
  const [started, setStarted] = React.useState(false);
  const [state, setState] = React.useState(null);
  const [sessionId, setSessionId] = React.useState(null);
  const [startedAt, setStartedAt] = React.useState(null);
  const [elapsed, setElapsed] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [pageError, setPageError] = React.useState('');
  const [completionDismissed, setCompletionDismissed] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const dailyPuzzle = await fetchDailyPuzzle();
        if (!active) return;
        const condition = Object.values(CONDITIONS).find((item) => item.key === dailyPuzzle.condition)
          || CONDITIONS.clearSkies;
        const puzzle = { ...dailyPuzzle, condition };
        setState(createGameState(puzzle));
        try {
          const sessionResponse = await fetchTodaySession();
          if (!active || !sessionResponse.session) return;
          const session = sessionResponse.session;
          setSessionId(session.id);
          setStarted(true);
          setStartedAt(Date.parse(session.started_at));
          setElapsed(Math.floor((session.elapsed_ms || 0) / 1000));
          setState((current) => ({
            ...current,
            lighthouses: session.lighthouses,
            strikes: session.strikes,
            completed: session.status === 'completed',
            canUndo: session.can_undo,
            canRedo: session.can_redo,
            feedback: session.status === 'completed' ? { type: 'completed' } : null,
          }));
        } catch (error) {
          if (active) {
            setPageError(error.status === 401
              ? 'Sign in through PrecisionPros Games to start today’s voyage.'
              : error.message);
          }
        }
      } catch (error) {
        if (active) setPageError(error.status === 503
          ? 'Today’s puzzle is not published yet. Please check back soon.'
          : 'We couldn’t load today’s puzzle. Please try again shortly.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    if (!startedAt || state?.completed) return undefined;
    const update = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [startedAt, state?.completed]);

  React.useEffect(() => {
    const preview = state?.previewedLighthouse;
    if (!preview) return undefined;
    const timer = window.setTimeout(() => {
      setState((current) => current.previewedLighthouse?.row === preview.row
        && current.previewedLighthouse?.column === preview.column
        ? { ...current, previewedLighthouse: null }
        : current);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [state?.previewedLighthouse]);

  React.useEffect(() => {
    const flash = state?.beamFlash;
    if (!flash) return undefined;
    const timer = window.setTimeout(() => {
      setState((current) => current.beamFlash?.row === flash.row
        && current.beamFlash?.column === flash.column
        ? { ...current, beamFlash: null }
        : current);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [state?.beamFlash]);

  React.useEffect(() => {
    const feedback = state?.feedback;
    if (!feedback?.type?.includes('conflict')) return undefined;
    const timer = window.setTimeout(() => {
      setState((current) => current.feedback === feedback
        ? { ...current, feedback: null }
        : current);
    }, 1800);
    return () => window.clearTimeout(timer);
  }, [state?.feedback]);

  React.useEffect(() => {
    if (!state?.completed || completionDismissed) return undefined;
    const dismissOnEscape = (event) => {
      if (event.key === 'Escape') setCompletionDismissed(true);
    };
    window.addEventListener('keydown', dismissOnEscape);
    return () => window.removeEventListener('keydown', dismissOnEscape);
  }, [state?.completed, completionDismissed]);

  const updateFromSession = (session, feedback, beamFlash = null) => {
    setState((current) => ({
      ...current,
      lighthouses: session.lighthouses,
      strikes: session.strikes,
      completed: session.status === 'completed',
      canUndo: session.can_undo,
      canRedo: session.can_redo,
      previewedLighthouse: null,
      beamFlash,
      feedback,
    }));
    setElapsed(Math.floor((session.elapsed_ms || 0) / 1000));
  };

  const begin = async () => {
    if (busy) return;
    setBusy(true);
    setPageError('');
    try {
      const response = await startDailySession();
      const session = response.session;
      setSessionId(session.id);
      setStartedAt(Date.parse(session.started_at));
      setStarted(true);
      updateFromSession(session, null);
    } catch (error) {
      setPageError(error.status === 401
        ? 'Sign in through PrecisionPros Games to start today’s voyage.'
        : error.message);
    } finally {
      setBusy(false);
    }
  };

  const actOnCell = async (row, column) => {
    if (busy || !state || state.completed || !sessionId) return;
    const occupied = hasLighthouse(state.lighthouses, row, column);
    if (occupied && (state.previewedLighthouse?.row !== row || state.previewedLighthouse?.column !== column)) {
      setState((current) => ({
        ...current,
        previewedLighthouse: { row, column },
        feedback: { type: 'beam-preview', row, column },
      }));
      return;
    }

    const action = occupied ? 'remove' : 'place';
    setBusy(true);
    setPageError('');
    try {
      const response = await performSessionAction(sessionId, action, row, column);
      updateFromSession(response.session, {
        type: action === 'place' ? 'placed' : 'removed', row, column,
      }, action === 'place' ? { row, column } : null);
    } catch (error) {
      const result = error.payload || {};
      if (result.session) {
        updateFromSession(result.session, {
          type: result.type || 'action-error', row: result.row ?? row, column: result.column ?? column,
        });
      } else {
        setPageError(error.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const runHistoryAction = async (action) => {
    if (busy || !sessionId || !state || state.completed) return;
    setBusy(true);
    setPageError('');
    try {
      const response = await performSessionAction(sessionId, action);
      updateFromSession(response.session, { type: action });
    } catch (error) {
      setPageError(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="sea-page" style={{ '--sea-accent': GAME_CONFIG.accent, '--sea-accent-dark': GAME_CONFIG.accentDark }}>
      <div className="sea-shell">
        <GameHeader
          game={GAME_CONFIG}
          homeHref={HUB_URL}
          links={[{
            key: 'seashroud-leaderboard',
            letter: 'S',
            color: GAME_CONFIG.accent,
            href: `${window.location.origin}/leaderboard`,
            played: true,
          }]}
        />

        {loading ? (
          <section className="sea-card welcome-card"><p className="sea-loading" role="status">Loading today’s puzzle…</p></section>
        ) : !state ? (
          <section className="sea-card welcome-card">
            <h1>Sea Shroud is unavailable</h1>
            <p className="sea-api-error" role="alert">{pageError || 'Today’s puzzle could not be loaded.'}</p>
            <a className="primary-action sea-action-link" href={HUB_URL}>Back to games</a>
          </section>
        ) : !started ? (
          <section className="sea-card welcome-card">
            <p className="eyebrow">Today&apos;s waters</p>
            <div className="condition-banner">
              <span className="condition-icon" aria-hidden="true">{state.puzzle.condition.icon}</span>
              <span><strong>{state.puzzle.condition.name}</strong><small>{state.puzzle.condition.schedule}</small></span>
            </div>
            <h1>How to Play Sea Shroud</h1>
            <div className="how-to-summary">
              <section>
                <h2>Goal</h2>
                <p>Place eight Lighthouses so every row, column, and color-patterned territory contains exactly one. Lighthouses cannot share a horizontal, vertical, or diagonal line.</p>
              </section>
              <section>
                <h2>Controls</h2>
                <ul>
                  <li>Tap an empty tile to place a <LighthouseIcon className="inline-lighthouse" title="Lighthouse" />. Its beams flash for about 3 seconds.</li>
                  <li>Tap a placed lighthouse once to preview its beams for 3 seconds; tap it again during that time to remove it. Beams never stay on permanently.</li>
                  <li>When a puzzle gets harder, look ahead before placing. You can use Undo to back out of a trial that leads to a dead end.</li>
                  <li>Visible rule violations are blocked and add a strike. The full territory map stays visible throughout the puzzle.</li>
                </ul>
              </section>
              <section>
                <h2>Daily Weather</h2>
                <ul className="weather-list">
                  <li><strong>⛵ Clear Skies (Mon–Wed):</strong> Direct forced moves keep the puzzle flowing.</li>
                  <li><strong>💨 Rolling Swells (Thu–Fri):</strong> Start with direct deductions; later moves reward looking ahead.</li>
                  <li><strong>🌫️ Deep Shroud (Sat–Sun):</strong> Tougher deductions may call for a trial placement and Undo.</li>
                </ul>
              </section>
              <section>
                <h2>Ranking</h2>
                <p>The puzzle ends automatically when all eight Lighthouses are correct.</p>
                <ul>
                  <li><strong>Flawless Beacons:</strong> Zero strikes, ranked by time.</li>
                  <li><strong>Navigators:</strong> Ranked by fewest strikes, then time.</li>
                </ul>
              </section>
            </div>
            {pageError && <p className="sea-api-error" role="alert">{pageError}</p>}
            <button type="button" className="primary-action" onClick={begin} disabled={busy || Boolean(pageError)}>{busy ? 'Starting…' : 'Begin Voyage'}</button>
            <p className="development-note">Starting your voyage records today’s attempt, even if you leave before solving.</p>
          </section>
        ) : (
          <section className="sea-card game-card">
            <div className="condition-chip"><span aria-hidden="true">{state.puzzle.condition.icon}</span> {state.puzzle.condition.name}</div>
            <div className="game-status" aria-label="Game status">
              <span><small>TIME</small><strong>{formatTime(elapsed)}</strong></span>
              <span><small>BEACONS</small><strong>{state.lighthouses.length}/8</strong></span>
              <span><small>STRIKES</small><strong>{state.strikes}</strong></span>
            </div>

            <p className="direct-play-instruction">Tap a tile to place or remove a lighthouse. Use Undo to retrace a trial placement.</p>
            {pageError && <p className="sea-api-error" role="alert">{pageError}</p>}

            <SeaBoard
              state={state}
              onCellAction={actOnCell}
            />

            <div className="history-actions">
              <button type="button" onClick={() => runHistoryAction('undo')} disabled={!state.canUndo || busy || state.completed}>Undo</button>
              <button type="button" onClick={() => runHistoryAction('redo')} disabled={!state.canRedo || busy || state.completed}>Redo</button>
            </div>

            <p className={`game-feedback ${state.feedback?.type?.includes('conflict') ? 'error' : ''}`} role="status" aria-live="polite">
              {state.completed
                ? `All waters charted. Completed in ${formatTime(elapsed)} with ${state.strikes} strikes. `
                : feedbackMessages[state.feedback?.type] || 'Tap an empty tile to place a lighthouse.'}
            </p>
            {state.completed && <a className="leaderboard-result-link" href={`${window.location.origin}/leaderboard`}>View Leaderboards</a>}
          </section>
        )}
        {state?.completed && !completionDismissed && (
          <CompletionDialog
            elapsed={elapsed}
            strikes={state.strikes}
            onClose={() => setCompletionDismissed(true)}
          />
        )}
      </div>
    </main>
  );
}

export default App;
